import type Cropper from "cropperjs";
import { store, useAppDispatch, useAppSelector } from "@/store/store";
import { getCropBoxData, loadCropper } from "@/utils/imageEdit/crop/loadCropper";
import { CropperOptions } from "@/utils/imageEdit/crop/cropTypes";
import {
    cropHandleCropBoxChange,
    cropSetLeft,
    cropSetTop,
    cropSetPreview,
    cropSetCropBox,
    setPreview,
    flipReset,
    rotateReset,
    deleteAllOperations,
    cropReset,
    cropSetWidth,
    cropSetHeight,
} from "@/store/features/bulkResize/actions";
import { useBulkResizerImageProvider } from "../BulkResizerImageProvider";
import { sleep } from "@/utils/sleep";
import { AnalyticsHelper } from "@/utils/adsense/AnalyticsHelper";
import { getCropOperation } from "@/store/features/bulkResize/utils/operations/getCropOperation";
import { useTransformImage } from "@/hooks/bulkResizer/useTransformImage";
import { useEffect, useRef } from "react";
import { getRandomId } from "@/utils/getRandomId";

interface CropOptions {
    onCropComplete?: () => void;
}

export const useBulkResizerCrop = (imageId: string, options: CropOptions = {}) => {
    const dispatch = useAppDispatch();
    const image = useAppSelector((store) => store.bulkResize.images.find((image) => image.id === imageId));
    const { cropperRef, imageRef, operation, setOperation } = useBulkResizerImageProvider();
    const isCropping = operation === "crop";
    const isResetting = operation === "reset";
    const { transformImage } = useTransformImage();
    const isMountedRef = useRef(true);

    function handleCropBoxChange(width: number, height: number, top: number, left: number) {
        dispatch(
            cropHandleCropBoxChange({
                value: {
                    width: getScaledUpWidth(width),
                    height: getScaledUpHeight(height),
                    top: getScaledUpHeight(top),
                    left: getScaledUpWidth(left),
                },
                imageId,
            }),
        );
    }

    function handleCropDrag(top: number, left: number) {
        dispatch(cropSetTop({ value: getScaledUpHeight(top), imageId }));
        dispatch(cropSetLeft({ value: getScaledUpWidth(left), imageId }));
    }

    const getScaledUpHeight = (height: number): number => {
        if (!image) return height;
        const previewHeight =
            store.getState().bulkResize.images.find((image) => image.id === imageId)?.crop.preview?.height ||
            image.crop.preview?.height ||
            0;

        const heightRatio = image.original.height / previewHeight;

        const result = height * heightRatio;
        return result;
    };

    const getScaledUpWidth = (width: number): number => {
        if (!image) return width;

        const previewWidth =
            store.getState().bulkResize.images.find((image) => image.id === imageId)?.crop.preview?.width ||
            image.crop.preview?.width ||
            0;

        const widthRatio = image.original.width / previewWidth;

        return width * widthRatio;
    };

    function onCropReady(cropper: Cropper, aspectRatio?: number) {
        // @ts-ignore
        window.cropper = cropper;
        if (!aspectRatio) return;
        const { width: cropBoxWidth, height: cropBoxHeight } = cropper.getCanvasData();
        cropper.setCropBoxData({
            width: cropBoxWidth,
            height: cropBoxHeight,
        });

        dispatch(cropSetLeft({ value: 0, imageId }));
        dispatch(cropSetTop({ value: 0, imageId }));
        dispatch(cropSetWidth({ imageId, value: cropBoxWidth }));
        dispatch(cropSetHeight({ imageId, value: cropBoxHeight }));
    }

    const handleCrop = async () => {
        if (operation) return;
        setOperation("crop");
        await sleep();
        const cropper = cropperRef?.current;
        const image = store.getState().bulkResize.images.find((image) => image.id === imageId);
        if (!cropper || !image) return;
        AnalyticsHelper.collectData({
            pageKey: "bulk-resize-editor",
            event: "Crop",
            featureName: "Bulk Resizer",
            eventLabel: "Editor Page",
        });
        const previewId = image.crop.preview?.id;

        const { width, height, left, top } = getCropBoxData(cropper);
        const { width: cropperCanvasWidth, height: cropperCanvasHeight } = cropper.getCanvasData();

        const rotation = image.rotate.rotation;
        const cropBox = image.crop.cropBox;

        const originalWidth = image.initialDimensions.width;
        const originalHeight = image.initialDimensions.height;

        const isPortrait = Math.abs(rotation) === 90 || Math.abs(rotation) === 270;

        let widthRatio = 0;
        let heightRatio = 0;

        if (isPortrait) {
            if (cropBox) {
                const isCropBoxPortrait = Math.abs(cropBox.rotation) === 90 || Math.abs(cropBox.rotation) === 270;
                if (isCropBoxPortrait) {
                    widthRatio = cropBox.width / cropperCanvasWidth;
                    heightRatio = cropBox.height / cropperCanvasHeight;
                } else {
                    widthRatio = cropBox.height / cropperCanvasWidth;
                    heightRatio = cropBox.width / cropperCanvasHeight;
                }
            } else {
                widthRatio = originalHeight / cropperCanvasWidth;
                heightRatio = originalWidth / cropperCanvasHeight;
            }
        } else {
            if (cropBox) {
                const isCropBoxPortrait = Math.abs(cropBox.rotation) === 90 || Math.abs(cropBox.rotation) === 270;
                if (!isCropBoxPortrait) {
                    widthRatio = cropBox.width / cropperCanvasWidth;
                    heightRatio = cropBox.height / cropperCanvasHeight;
                } else {
                    widthRatio = cropBox.height / cropperCanvasWidth;
                    heightRatio = cropBox.width / cropperCanvasHeight;
                }
            } else {
                widthRatio = originalWidth / cropperCanvasWidth;
                heightRatio = originalHeight / cropperCanvasHeight;
            }
        }

        const x = widthRatio * left;
        const y = heightRatio * top;
        const _width = widthRatio * width;
        const _height = heightRatio * height;

        const newCropBox = {
            x,
            y,
            width: _width,
            height: _height,
            rotation,
            src: "",
        } as const;

        const newOperation = getCropOperation(image, newCropBox);
        const blobUrl = await transformImage(image.id, newOperation);

        cropper.destroy();
        cropperRef.current = null;

        if (
            isMountedRef.current &&
            previewId === store.getState().bulkResize.images.find(({ id }) => id === image.id)?.crop.preview?.id
        ) {
            dispatch(setPreview({ id: imageId, value: { src: blobUrl } }));

            dispatch(
                cropSetCropBox({
                    imageId,
                    cropBox: newCropBox,
                }),
            );
            options?.onCropComplete?.();
        }

        setOperation(null);

        await sleep();
    };

    const handleReset = async () => {
        if (operation) return;
        const cropper = cropperRef?.current;
        if (!cropperRef || !image || !cropper || image.original.src === image.preview.src) return;
        setOperation("reset");
        await sleep();

        cropper.destroy();
        cropperRef.current = null;

        dispatch(cropSetCropBox({ imageId, cropBox: null }));
        dispatch(flipReset({ imageId }));
        dispatch(rotateReset({ imageId }));
        dispatch(deleteAllOperations({ imageId }));
        dispatch(cropReset({ imageId }));

        dispatch(setPreview({ id: imageId, value: { src: image.original.src } }));

        setOperation(null);

        document.body.dispatchEvent(new Event("crop-reset-complete"));
    };

    const getOptions = (aspectRatio?: number): CropperOptions => ({
        aspectRatio: aspectRatio ?? 0,
        zoomable: false,
        crop: handleCropBoxChange,
        cropdrag: handleCropDrag,
        cropresize: handleCropBoxChange,
        ready: (cropper) => onCropReady(cropper, aspectRatio),
        /**
         * `viewMode: 2` prevents the rectangle from going out of the image
         */
        viewMode: 2,
        minContainerHeight: 1,
        minContainerWidth: 1,
        background: false,
    });

    const handleLoadCropper = (aspectRatio?: number): Cropper | null => {
        if (!cropperRef || !imageRef?.current || !image) return null;
        const { cropper } = loadCropper(imageRef.current, getOptions(aspectRatio));
        cropperRef.current = cropper;
        return cropper;
    };

    const onImageLoad = () => {
        dispatch(
            cropSetPreview({
                imageId,
                value: {
                    width: imageRef?.current?.getBoundingClientRect().width || 0,
                    height: imageRef?.current?.getBoundingClientRect().height || 0,
                    id: crypto.randomUUID(),
                },
            }),
        );
        // facing weird bug without this
        setTimeout(() => {
            const cropBox = store.getState().resizeEditor.crop.cropBox;
            if (cropBox) {
                const { width, height } = cropBox;
                handleLoadCropper(width / height);
            } else {
                handleLoadCropper();
            }
        }, 50);
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return { imageRef, onImageLoad, handleCrop, handleReset, isCropping, isResetting };
};
